import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithPopup, createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { auth, googleProvider } from "../../../firestore-config";
import { useStore } from "../../../app/stores/rootStore";

export default observer(function UserRegister() {
    const { userStore, cartStore, wishlistStore } = useStore();
    const navigate = useNavigate();
    const [displayName, setDisplayName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) return setError("Passwords do not match.");
        
        setLoading(true);
        setError("");

        try {
            // 1. Create User in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            
            // 2. Update Profile with Display Name
            await updateProfile(userCredential.user, { displayName: displayName });

            // 3. UserStore listener will handle state and sync Cart/Wishlist automatically
            navigate("/dashboard");
        } catch (err: any) {
            console.error(err);
            setError(err.message || "Registration failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            await signInWithPopup(auth, googleProvider);
            navigate("/dashboard");
        } catch (err) {
            setError("Google sign-up failed.");
        }
    };

    return (
        <section className="form1 cid-uATOSlQKYb py-5 bg-light min-vh-100 d-flex align-items-center">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-6 col-md-8">
                        <div className="card border-0 shadow-lg rounded-4 p-4 p-md-5 bg-white">
                            <div className="text-center mb-4">                                
                                <h2 className="fw-bold mbr-section-title">Create Account</h2>
                                <p className="text-muted small">Join us to save your favorites and track orders</p>
                            </div>

                            {error && <div className="alert alert-danger small py-2">{error}</div>}

                            <form onSubmit={handleRegister}>
                                <div className="row">
                                    <div className="col-12 mb-3">
                                        <label className="form-label small fw-bold">Full Name</label>
                                        <input 
                                            type="text" 
                                            className="form-control form-control-lg rounded-3 fs-6" 
                                            placeholder="John Doe"
                                            value={displayName}
                                            onChange={(e) => setDisplayName(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label small fw-bold">Email Address</label>
                                        <input 
                                            type="email" 
                                            className="form-control form-control-lg rounded-3 fs-6" 
                                            placeholder="name@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label small fw-bold">Password</label>
                                        <input 
                                            type="password" 
                                            className="form-control form-control-lg rounded-3 fs-6" 
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <label className="form-label small fw-bold">Confirm Password</label>
                                        <input 
                                            type="password" 
                                            className="form-control form-control-lg rounded-3 fs-6" 
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                <button 
                                    type="submit" 
                                    className="btn btn-danger btn-lg w-100 rounded-pill mb-3 py-3 fw-bold shadow-sm"
                                    disabled={loading}
                                >
                                    {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : "Register Now"}
                                </button>
                            </form>

                            <div className="text-center my-3 position-relative">
                                <hr />
                                <span className="position-absolute top-50 start-50 translate-middle bg-white px-3 text-muted small">OR SIGN UP WITH</span>
                            </div>

                            <button 
                                className="btn btn-white w-100 rounded-pill py-3 d-flex align-items-center justify-content-center border shadow-sm mb-3"
                                onClick={handleGoogleSignIn}
                            >
                                <img src="www.gstatic.com" 
                                     alt="Google" className="me-2" style={{width: '18px'}} />
                                <span className="fw-semibold">Google Account</span>
                            </button>

                            <p className="text-center mt-4 mb-0 small text-muted">
                                Already have an account? <Link to="/login" className="text-danger fw-bold text-decoration-none">Login Here</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
});